import SitesContext, {
  currentSiteIdAtom,
  useSite,
} from "@src/context/SitesContext";
import backpackSdk from "@src/global_functions/backpackSdk/sdk";
import { useSearchParamAtom, useStorage } from "@src/global_functions/hooks";
import { atom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";

interface Account {
  id: number;
  label: string;
}
interface CurrentAccountState {
  isLoading: boolean;
  account?: Account;
  accountId?: number;
  accounts?: Account[];
  setAccountId?: (accountId?: number) => void;
}
const currentAccountStateAtom = atom<CurrentAccountState>({
  isLoading: true,
});
export function useCurrentAccount() {
  return useAtomValue(currentAccountStateAtom);
}
export function useCurrentAccountSites() {
  const { currentAccountSites, isLoading: loading } = useContext(SitesContext);

  return {
    allSites: currentAccountSites,
    isLoading: loading,
  };
}

export const CURRENT_ACCOUNT_STORAGE_KEY = "currentAccountId";

function CurrentAccountManagerComponent({
  isLoading,
  accounts,
}: {
  readonly isLoading: boolean;
  readonly accounts?: Account[];
}) {
  /*
   * Current Account can come from 4 sources, ordered by precedence:
   *
   * 1. Current site's account (/site/:siteId/)
   * 2. Account ID search param (?accountId=123)
   * 3. Local storage (most recent account ID)
   * 4. First account in list of accounts that the current user has access to
   */
  const setCurrentAccountState = useSetAtom(currentAccountStateAtom);
  const history = useHistory();
  const currentSiteId = useAtomValue(currentSiteIdAtom);
  const currentSite = useSite(currentSiteId);
  const [searchParamAccountId, setSearchParamAccountId] = useSearchParamAtom(
    "accountId",
    Number
  );

  const accountFromCurrentSite = useMemo(() => {
    if (!currentSite?.accountId) {
      return null;
    }
    return {
      id: currentSite.accountId,
      label: currentSite.accountName,
    };
  }, [currentSite]);

  const accountFromSearchParam = useMemo(
    () =>
      searchParamAccountId
        ? accounts?.find((e) => e.id === searchParamAccountId)
        : undefined,
    [accounts, searchParamAccountId]
  );

  const [storedAccountId, setStoredAccountId] = useStorage<number | undefined>(
    CURRENT_ACCOUNT_STORAGE_KEY,
    undefined
  );
  const accountFromLocalStorage = useMemo(
    () =>
      storedAccountId
        ? accounts?.find((e) => e.id === storedAccountId)
        : undefined,
    [accounts, storedAccountId]
  );

  const accountFromFirstInList = accounts?.[0];

  const account =
    accountFromCurrentSite ??
    accountFromSearchParam ??
    accountFromLocalStorage ??
    accountFromFirstInList;
  const accountId = account?.id;

  useEffect(() => {
    if (accountId !== undefined && accountId !== storedAccountId) {
      setStoredAccountId(accountId);
    }
  }, [accountId, setStoredAccountId, storedAccountId]);

  const setAccountId = useCallback(
    (nextAccountId: any) => {
      if (nextAccountId === accountId) {
        return;
      }
      if (accountFromCurrentSite) {
        // Current route determines the account, so we must redirect to homepage if account changes
        history.push("/");
      } else {
        // Clear the previous account's filters/query params so they don't carry over
        history.replace({ search: "" });
      }
      setSearchParamAccountId(nextAccountId);
    },
    [accountId, setSearchParamAccountId, accountFromCurrentSite, history]
  );

  useEffect(() => {
    setCurrentAccountState({
      isLoading,
      account,
      accountId,
      accounts,
      setAccountId,
    });
  }, [
    setCurrentAccountState,
    isLoading,
    account,
    accountId,
    accounts,
    setAccountId,
  ]);

  return null;
}

/*
 * Manages the current account shown in the top nav and used on most pages
 * Acts as a provider for useCurrentAccount hook
 */
export function CurrentAccountManager() {
  const { data, isLoading } = backpackSdk.accounts.accounts.useQueryAll();

  const accounts = useMemo(() => {
    const result = data?.map(({ id, name }) => ({
      id,
      label: name,
    }));
    result?.sort(({ label: a }, { label: b }) => a.localeCompare(b));
    return result;
  }, [data]);

  return (
    <CurrentAccountManagerComponent accounts={accounts} isLoading={isLoading} />
  );
}
